// Sign-in, sign-up, email links, password reset, invitations and the first organization.
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { APP_NAME, ApiError, api, type Me, type Role } from "../../lib/api";
import { useMe, useSetMe } from "../../lib/session";
import { JoinableOrgs } from "../org/joinable";
import { SignInAnnouncements } from "../../components/announcements";
import { Alert, Button, Card, Field, Logo, Spinner } from "../../components/ui";

export function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <div className="mb-8"><Logo /></div>
      <Card className="w-full max-w-md">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </Card>
      {footer && <div className="mt-6 text-sm text-slate-600 dark:text-slate-400">{footer}</div>}
    </div>
  );
}

// "Continue with Google / Microsoft" (only the providers the server has keys for). A full page navigation: the server
// sends the browser on to the provider and back.
type Provider = { id: "google" | "microsoft"; label: string };
type AuthOptions = { providers: Provider[]; signup?: { open: boolean; message: string | null } };
const useAuthOptions = () => useQuery({ queryKey: ["providers"], queryFn: () => api<AuthOptions>("/auth/providers"), staleTime: 300_000 }).data;
export function useProviders() {
  return useAuthOptions()?.providers ?? [];
}
// Whether new people may sign up and start organizations (the platform's admins can close it; invitations still work).
export function useSignup() {
  return useAuthOptions()?.signup ?? { open: true, message: null };
}
const CLOSED_HINT = "If you've been invited, use the link in your invitation email.";
export function ProviderLogo({ id }: { id: Provider["id"] }) {
  return id === "google" ? (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
  ) : (
    <svg viewBox="0 0 23 23" className="h-5 w-5" aria-hidden="true"><path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/></svg>
  );
}
function ProviderButtons({ next }: { next: string | null }) {
  const providers = useProviders();
  if (!providers.length) return null;
  return (
    <div className="space-y-2">
      {providers.map((p) => (
        <a key={p.id} href={`/api/v1/auth/oauth/${p.id}/start?next=${encodeURIComponent(next ?? "/")}`}
          className="flex w-full items-center justify-center gap-3 rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700 dark:hover:bg-slate-700">
          <ProviderLogo id={p.id} />Continue with {p.label}
        </a>
      ))}
    </div>
  );
}
const OAUTH_ERRORS: Record<string, string> = {
  cancelled: "Sign-in was cancelled.",
  email_unverified: "That account's email address isn't verified by the provider, so it can't be used to sign in to an existing account here. Sign in with your password or an email link instead.",
  no_email: "The provider didn't share an email address, which we need to create your account.",
  linked_elsewhere: "That account is already connected to a different user here.",
  disabled: "This account has been disabled.",
  expired: "The sign-in took too long. Please try again.",
  bad_state: "The sign-in couldn't be completed (it may have been started in another tab). Please try again.",
  not_configured: "That sign-in option isn't available.",
  signup_closed: `Sign-up is closed for now, so there's no account for that address. ${CLOSED_HINT}`,
};

// Form state: submitting, the top message and per-field messages from the API.
function useForm() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      return await fn();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        setFields(e.fields);
      } else {
        setError("Something went wrong. Please try again.");
      }
      return undefined;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, fields, run, setError };
}

const value = (form: HTMLFormElement, name: string) => String(new FormData(form).get(name) ?? "");

// Where to go once signed in: back to the page that sent you here, else your organization (or create one).
function homeFor(me: Me | null | undefined, next?: string | null) {
  if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  if (!me) return "/login";
  const first = me.organizations.find((o) => !o.suspended);
  return first ? `/o/${first.slug}` : me.organizations[0] ? `/o/${me.organizations[0].slug}` : "/welcome";
}

export function Home() {
  const me = useMe();
  if (me.isLoading) return <FullPageSpinner />;
  return <Navigate to={homeFor(me.data)} replace />;
}

export function FullPageSpinner() {
  return <div className="grid min-h-dvh place-items-center text-brand-600"><Spinner className="h-8 w-8" /></div>;
}

function Divider({ label }: { label: string }) {
  return (
    <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wide text-slate-400">
      <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />{label}<span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

// What signing in answers: you're in, or (two-factor on) a code is needed next.
type SignIn = Me | { mfaRequired: true };
const needsCode = (r: SignIn): r is { mfaRequired: true } => "mfaRequired" in r;

export function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setMe = useSetMe();
  const me = useMe();
  const form = useForm();
  const [unverified, setUnverified] = useState<string | null>(null);
  const [askCode, setAskCode] = useState(params.get("mfa") === "1");
  const next = params.get("next");
  const oauthError = params.get("error");
  const providers = useProviders();
  const signup = useSignup();
  if (me.data) return <Navigate to={homeFor(me.data, next)} replace />;
  if (askCode) return <CodeStep next={next} />;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = value(e.currentTarget, "email");
    setUnverified(null);
    const result = await form.run(async () => {
      try {
        return await api<SignIn>("/auth/login", { method: "POST", body: { email, password: value(e.currentTarget, "password") } });
      } catch (err) {
        if (err instanceof ApiError && err.code === "EMAIL_NOT_VERIFIED") setUnverified(email);
        throw err;
      }
    });
    if (result && needsCode(result)) setAskCode(true);
    else if (result) {
      setMe(result);
      navigate(homeFor(result, next), { replace: true });
    }
  }

  async function resend() {
    if (!unverified) return;
    await form.run(() => api("/auth/resend-verification", { method: "POST", body: { email: unverified } }));
    navigate(`/check-email?email=${encodeURIComponent(unverified)}&reason=verify`);
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle={`Welcome back to ${APP_NAME}.`}
      footer={signup.open ? <>New here? <Link className="font-medium text-brand-600 hover:underline" to={`/signup${next ? `?next=${encodeURIComponent(next)}` : ""}`}>Create an account</Link></> : undefined}
    >
      <SignInAnnouncements />
      {oauthError && <div className="mb-4"><Alert>{OAUTH_ERRORS[oauthError] ?? "Signing in with that account didn't work. Please try again."}</Alert></div>}
      <ProviderButtons next={next} />
      {providers.length > 0 && <Divider label="or" />}
      <form onSubmit={submit} className="space-y-4" noValidate>
        {form.error && <Alert>{form.error}{unverified && <> <button type="button" onClick={resend} className="font-medium underline">Send the link again</button></>}</Alert>}
        <Field label="Email" name="email" type="email" autoComplete="email" required autoFocus defaultValue={params.get("email") ?? ""} error={form.fields.email} />
        <Field label="Password" name="password" type="password" autoComplete="current-password" required error={form.fields.password} />
        <div className="flex justify-end"><Link to="/forgot-password" className="text-sm text-brand-600 hover:underline">Forgot your password?</Link></div>
        <Button type="submit" className="w-full" loading={form.busy}>Sign in</Button>
      </form>
      <Divider label="or" />
      <Link to="/magic-link" className="block"><Button variant="secondary" className="w-full" type="button">Email me a sign-in link</Button></Link>
    </AuthLayout>
  );
}

// The second step of signing in: a code from the authenticator app, or a recovery code.
function CodeStep({ next }: { next: string | null }) {
  const navigate = useNavigate();
  const setMe = useSetMe();
  const form = useForm();
  const [recovery, setRecovery] = useState(false);
  const [expired, setExpired] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const code = value(e.currentTarget, "code");
    const me = await form.run(async () => {
      try { return await api<Me>("/auth/mfa/verify", { method: "POST", body: { code } }); } catch (err) {
        if (err instanceof ApiError && err.code === "MFA_EXPIRED") setExpired(true);
        throw err;
      }
    });
    if (me) { setMe(me); navigate(homeFor(me, next), { replace: true }); }
  }
  return (
    <AuthLayout
      title="Two-factor sign-in"
      subtitle={recovery ? "Enter one of the recovery codes you saved when you turned on two-factor sign-in." : "Open your authenticator app and enter the 6-digit code for this account."}
      footer={<Link className="text-brand-600 hover:underline" to={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`} reloadDocument>Cancel and start again</Link>}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {form.error && <Alert>{form.error}{expired && <> <Link to="/login" reloadDocument className="font-medium underline">Sign in again</Link></>}</Alert>}
        <Field key={recovery ? "rc" : "app"} label={recovery ? "Recovery code" : "Code"} name="code" required autoFocus autoComplete="one-time-code"
          inputMode={recovery ? "text" : "numeric"} maxLength={recovery ? 11 : 6} placeholder={recovery ? "abcde-fghjk" : "123456"} error={form.fields.code} />
        <Button type="submit" className="w-full" loading={form.busy} disabled={expired}>Verify</Button>
      </form>
      <button type="button" onClick={() => setRecovery((r) => !r)} className="mt-4 w-full text-center text-sm text-brand-600 hover:underline">
        {recovery ? "Use the code from my app instead" : "Lost your phone? Use a recovery code"}
      </button>
    </AuthLayout>
  );
}

export function Signup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setMe = useSetMe();
  const me = useMe();
  const form = useForm();
  const inviteToken = params.get("invite");
  const providers = useProviders();
  const signup = useSignup();
  const next = params.get("next");
  if (me.data && !inviteToken) return <Navigate to={homeFor(me.data, next)} replace />;
  if (!signup.open && !inviteToken) {
    return (
      <AuthLayout title="Sign-up is closed" footer={<>Already have an account? <Link className="font-medium text-brand-600 hover:underline" to="/login">Sign in</Link></>}>
        {signup.message && <p className="whitespace-pre-line text-sm">{signup.message}</p>}
        <p className={signup.message ? "mt-3 text-sm text-slate-600 dark:text-slate-400" : "text-sm"}>{signup.message ? CLOSED_HINT : `New accounts can't be created on ${APP_NAME} right now. ${CLOSED_HINT}`}</p>
      </AuthLayout>
    );
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = value(e.currentTarget, "email");
    const body = { name: value(e.currentTarget, "name"), email, password: value(e.currentTarget, "password"), ...(inviteToken ? { inviteToken } : {}) };
    const result = await form.run(() => api<Me & { ok?: boolean; joined?: { slug: string; projectId?: string | null } }>("/auth/signup", { method: "POST", body }));
    if (!result) return;
    if (result.user) {
      setMe({ user: result.user, organizations: result.organizations });
      navigate(result.joined ? joinedHome(result.joined) : homeFor(result, next), { replace: true });
    } else {
      navigate(`/check-email?email=${encodeURIComponent(email)}&reason=verify`);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Meetings, calls and chat for your team."
      footer={<>Already have an account? <Link className="font-medium text-brand-600 hover:underline" to="/login">Sign in</Link></>}
    >
      <ProviderButtons next={inviteToken ? `/invite?token=${inviteToken}` : next} />
      {providers.length > 0 && <Divider label="or" />}
      <form onSubmit={submit} className="space-y-4" noValidate>
        {form.error && <Alert>{form.error}</Alert>}
        <Field label="Your name" name="name" autoComplete="name" required autoFocus error={form.fields.name} />
        <Field label="Work email" name="email" type="email" autoComplete="email" required defaultValue={params.get("email") ?? ""} readOnly={Boolean(inviteToken && params.get("email"))} error={form.fields.email} />
        <Field label="Password" name="password" type="password" autoComplete="new-password" required minLength={10} error={form.fields.password} hint="At least 10 characters." />
        <Button type="submit" className="w-full" loading={form.busy}>Create account</Button>
        <p className="text-center text-xs text-slate-500">By creating an account you agree to the terms of service.</p>
      </form>
    </AuthLayout>
  );
}

export function CheckEmail() {
  const [params] = useSearchParams();
  const email = params.get("email");
  const reason = params.get("reason");
  const what = reason === "magic" ? "a sign-in link" : reason === "reset" ? "a link to choose a new password" : "a link to confirm your email address";
  return (
    <AuthLayout title="Check your email" footer={<Link className="text-brand-600 hover:underline" to="/login">Back to sign in</Link>}>
      <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-700/30" aria-hidden="true">
          <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 6h16v12H4z" /><path d="m4 7 8 6 8-6" /></svg>
        </div>
        <p>If {email ? <strong>{email}</strong> : "that address"} can be used, we've sent {what}. It may take a minute to arrive; check your spam folder too.</p>
      </div>
    </AuthLayout>
  );
}

// Opening an emailed link: use the token once, then sign in.
function useTokenAction(path: string) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setMe = useSetMe();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);
  const token = params.get("token");
  useEffect(() => {
    if (ran.current || !token) return;
    ran.current = true;
    api<SignIn>(path, { method: "POST", body: { token } })
      .then((r) => { if (needsCode(r)) navigate("/login?mfa=1", { replace: true }); else { setMe(r); navigate(homeFor(r), { replace: true }); } })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Something went wrong."));
  }, [token, path, navigate, setMe]);
  return { token, error };
}

export function VerifyEmail() {
  const { token, error } = useTokenAction("/auth/verify-email");
  return (
    <AuthLayout title="Confirming your email" footer={<Link className="text-brand-600 hover:underline" to="/login">Back to sign in</Link>}>
      {!token ? <Alert>This link is incomplete. Open it again from the email.</Alert> : error ? <Alert>{error}</Alert> : <div className="flex justify-center py-6 text-brand-600"><Spinner className="h-7 w-7" /></div>}
    </AuthLayout>
  );
}

export function MagicLink() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const form = useForm();
  if (params.get("token")) return <MagicLinkConsume />;
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = value(e.currentTarget, "email");
    const ok = await form.run(() => api("/auth/magic-link", { method: "POST", body: { email } }));
    if (ok) navigate(`/check-email?email=${encodeURIComponent(email)}&reason=magic`);
  }
  return (
    <AuthLayout title="Sign in with an email link" subtitle="We'll email you a link that signs you in - no password needed." footer={<Link className="text-brand-600 hover:underline" to="/login">Sign in with a password instead</Link>}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        {form.error && <Alert>{form.error}</Alert>}
        <Field label="Email" name="email" type="email" autoComplete="email" required autoFocus error={form.fields.email} />
        <Button type="submit" className="w-full" loading={form.busy}>Email me a link</Button>
      </form>
    </AuthLayout>
  );
}

function MagicLinkConsume() {
  const { error } = useTokenAction("/auth/magic-link/consume");
  return (
    <AuthLayout title="Signing you in" footer={<Link className="text-brand-600 hover:underline" to="/magic-link">Ask for a new link</Link>}>
      {error ? <Alert>{error}</Alert> : <div className="flex justify-center py-6 text-brand-600"><Spinner className="h-7 w-7" /></div>}
    </AuthLayout>
  );
}

export function ForgotPassword() {
  const navigate = useNavigate();
  const form = useForm();
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = value(e.currentTarget, "email");
    const ok = await form.run(() => api("/auth/password/forgot", { method: "POST", body: { email } }));
    if (ok) navigate(`/check-email?email=${encodeURIComponent(email)}&reason=reset`);
  }
  return (
    <AuthLayout title="Forgot your password?" subtitle="Enter your email and we'll send you a link to choose a new one." footer={<Link className="text-brand-600 hover:underline" to="/login">Back to sign in</Link>}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        {form.error && <Alert>{form.error}</Alert>}
        <Field label="Email" name="email" type="email" autoComplete="email" required autoFocus error={form.fields.email} />
        <Button type="submit" className="w-full" loading={form.busy}>Send reset link</Button>
      </form>
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setMe = useSetMe();
  const form = useForm();
  const token = params.get("token");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const password = value(e.currentTarget, "password");
    if (password !== value(e.currentTarget, "confirm")) {
      form.setError("The two passwords don't match.");
      return;
    }
    const r = await form.run(() => api<SignIn>("/auth/password/reset", { method: "POST", body: { token, password } }));
    if (r && needsCode(r)) navigate("/login?mfa=1", { replace: true });
    else if (r) { setMe(r); navigate(homeFor(r), { replace: true }); }
  }
  return (
    <AuthLayout title="Choose a new password" subtitle="You'll be signed out everywhere else." footer={<Link className="text-brand-600 hover:underline" to="/forgot-password">Ask for a new link</Link>}>
      {!token ? <Alert>This link is incomplete. Open it again from the email.</Alert> : (
        <form onSubmit={submit} className="space-y-4" noValidate>
          {form.error && <Alert>{form.error}</Alert>}
          <Field label="New password" name="password" type="password" autoComplete="new-password" required autoFocus error={form.fields.password} hint="At least 10 characters." />
          <Field label="Repeat it" name="confirm" type="password" autoComplete="new-password" required />
          <Button type="submit" className="w-full" loading={form.busy}>Save and sign in</Button>
        </form>
      )}
    </AuthLayout>
  );
}

type InvitePreview = { organization: { name: string; slug: string }; email: string; role: Role; invitedBy: string | null; project?: { id: string; name: string } | null };

// Where an accepted invitation leads: a client's, straight to the project they were invited to.
const joinedHome = (j: { slug: string; projectId?: string | null }) => (j.projectId ? `/o/${j.slug}/projects/${j.projectId}` : `/o/${j.slug}`);

export function Invite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  const setMe = useSetMe();
  const form = useForm();
  const token = params.get("token") ?? "";
  const preview = useQuery({ queryKey: ["invite", token], queryFn: () => api<InvitePreview>(`/invitations/${encodeURIComponent(token)}`), enabled: Boolean(token), retry: false });

  if (!token) return <AuthLayout title="Invitation"><Alert>This link is incomplete. Open it again from the email.</Alert></AuthLayout>;
  if (preview.isLoading || me.isLoading) return <FullPageSpinner />;
  if (preview.error) return <AuthLayout title="Invitation" footer={<Link className="text-brand-600 hover:underline" to="/">Go to {APP_NAME}</Link>}><Alert>{(preview.error as Error).message}</Alert></AuthLayout>;
  const inv = preview.data!;
  const here = `${location.pathname}${location.search}`;

  async function accept() {
    const result = await form.run(() => api<{ joined: { slug: string; projectId?: string | null } }>(`/invitations/${encodeURIComponent(token)}/accept`, { method: "POST" }));
    if (result) {
      setMe(await api<Me>("/auth/me"));
      navigate(joinedHome(result.joined), { replace: true });
    }
  }

  const signedInAs = me.data?.user.email;
  return (
    <AuthLayout
      title={inv.project ? `Join ${inv.project.name}` : `Join ${inv.organization.name}`}
      subtitle={inv.project
        ? <>{inv.invitedBy ?? "Someone"} at {inv.organization.name} invited <strong>{inv.email}</strong> to the project, to follow its progress, ask for things and approve work.</>
        : <>{inv.invitedBy ?? "Someone"} invited <strong>{inv.email}</strong> to join as {inv.role === "guest" ? "a guest" : `a${inv.role === "admin" ? "n" : ""} ${inv.role}`}.</>}
    >
      <div className="space-y-4">
        {form.error && <Alert>{form.error}</Alert>}
        {signedInAs === inv.email ? (
          <Button className="w-full" onClick={accept} loading={form.busy}>Accept and join</Button>
        ) : signedInAs ? (
          <Alert kind="info">You're signed in as {signedInAs}. Sign out, then sign in as {inv.email} to accept.</Alert>
        ) : (
          <>
            <Link to={`/signup?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(inv.email)}`} className="block"><Button className="w-full" type="button">Create an account and join</Button></Link>
            <Link to={`/login?email=${encodeURIComponent(inv.email)}&next=${encodeURIComponent(here)}`} className="block"><Button variant="secondary" className="w-full" type="button">I already have an account</Button></Link>
          </>
        )}
      </div>
    </AuthLayout>
  );
}

export function Welcome() {
  const navigate = useNavigate();
  const me = useMe();
  const signup = useSignup();
  const setMe = useSetMe();
  const form = useForm();
  if (me.isLoading) return <FullPageSpinner />;
  if (!me.data) return <Navigate to="/login?next=/welcome" replace />;
  if (!signup.open && !me.data.user.platformRole) {
    return (
      <AuthLayout title="New organizations are paused" subtitle={`New organizations can't be created on ${APP_NAME} right now. To join one, ask its admin to invite you.`}>
        <JoinableOrgs />
        {signup.message && <p className="whitespace-pre-line text-sm text-slate-600 dark:text-slate-400">{signup.message}</p>}
      </AuthLayout>
    );
  }
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const result = await form.run(() => api<{ organization: { slug: string } }>("/orgs", { method: "POST", body: { name: value(e.currentTarget, "name") } }));
    if (result) {
      setMe(await api<Me>("/auth/me"));
      navigate(`/o/${result.organization.slug}`, { replace: true });
    }
  }
  return (
    <AuthLayout title="Create your organization" subtitle={`Hi ${me.data.user.name.split(" ")[0]}! An organization is where your team meets, calls and chats. You can invite people next.`}>
      <JoinableOrgs />
      <form onSubmit={submit} className="space-y-4" noValidate>
        {form.error && <Alert>{form.error}</Alert>}
        <Field label="Organization name" name="name" required autoFocus placeholder="e.g. Acme Inc." error={form.fields.name} />
        <Button type="submit" className="w-full" loading={form.busy}>Create organization</Button>
      </form>
    </AuthLayout>
  );
}
